import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import { UserContext } from "../../../context/UserContext";
import { axiosInstance } from "../../../utils/networking";
import useReportFilter, { RANGE_PRESETS, RangePreset } from "./useReportFilter";
import DrilldownDrawer, { DrilldownTarget } from "./DrilldownDrawer";
import { ConsultantOption } from "./types";
import OverviewTab from "./tabs/OverviewTab";
import ConsultantsTab from "./tabs/ConsultantsTab";
import ClientsTab from "./tabs/ClientsTab";
import ReferralsTab from "./tabs/ReferralsTab";
import IntakeTab from "./tabs/IntakeTab";
import DataTab from "./tabs/DataTab";

const PRIMARY = "#9D2362";
const MAGENTA_TINT = "#F9E6F0";
const CARD_BORDER = "1px solid #ececf0";

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "consultants", label: "Consultants" },
  { value: "clients", label: "Clients" },
  { value: "resource-referrals", label: "Resource Referrals" },
  { value: "b2b-referrals", label: "B2B Referrals" },
  { value: "intake", label: "Intake & Surveys" },
  { value: "data", label: "Data" },
];

/**
 * Legacy deep links. The single "Referrals" tab was split into one tab per
 * partner kind; `?tab=referrals` lands on the resource half rather than
 * silently dumping the visitor back on Overview.
 */
const TAB_ALIASES: { [legacy: string]: string } = {
  referrals: "resource-referrals",
};

/** Tabs whose content is driven by the shared date filter. */
const FILTER_AWARE_TABS = new Set([
  "consultants",
  "clients",
  "resource-referrals",
  "b2b-referrals",
  "intake",
]);

/**
 * The Reporting hub — one page replacing Program Statistics, the old Reports
 * index and the Executive dashboard. Every filter lives in the URL:
 *
 *   /dashboard/reports?tab=&sub=&range=&start=&end=&consultant=
 */
const ReportingLayout: React.FC = () => {
  const { user } = useContext(UserContext);
  const isAdmin = user?.role === "admin";
  const filter = useReportFilter();
  const [drilldown, setDrilldown] = useState<DrilldownTarget | null>(null);
  const [consultants, setConsultants] = useState<ConsultantOption[]>([]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    axiosInstance
      .get("/user/allUsers")
      .then((res) => {
        if (cancelled) return;
        const options: ConsultantOption[] = (res.data?.users || [])
          .filter((u: any) => u.role === "bfc" || u.role === "admin")
          .map((u: any) => ({
            _id: String(u._id),
            name: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
            firstName: u.firstName || "",
            lastName: u.lastName || "",
            email: u.email,
            role: u.role,
          }))
          .filter((o: ConsultantOption) => o.name)
          .sort((a: ConsultantOption, b: ConsultantOption) =>
            a.name.localeCompare(b.name),
          );
        setConsultants(options);
      })
      .catch((err) => console.error("Failed to load consultants:", err));
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const requestedTab = TAB_ALIASES[filter.tab] || filter.tab;
  const activeTab = TABS.some((t) => t.value === requestedTab)
    ? requestedTab
    : "overview";

  const tabProps = useMemo(
    () => ({
      start: filter.start,
      end: filter.end,
      consultant: filter.consultant,
      onDrill: (target: DrilldownTarget) => setDrilldown(target),
    }),
    [filter.start, filter.end, filter.consultant],
  );

  const showsOwnFilters =
    !FILTER_AWARE_TABS.has(activeTab) && activeTab !== "overview";

  return (
    <Box sx={{ backgroundColor: "#f6f7f9", p: { xs: 1, md: 2 }, minHeight: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "16px",
          border: CARD_BORDER,
          backgroundColor: "white",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter.preset}
            onChange={(_, value: RangePreset | null) =>
              value && filter.setPreset(value)
            }
            sx={{
              "& .Mui-selected": {
                backgroundColor: `${MAGENTA_TINT} !important`,
                color: PRIMARY,
                fontWeight: 700,
              },
            }}
          >
            {RANGE_PRESETS.map((preset) => (
              <ToggleButton key={preset.value} value={preset.value}>
                {preset.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {filter.preset === "custom" && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={filter.start}
                onChange={(d: Dayjs | null) => filter.setStart(d)}
                disableFuture
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label="End Date"
                value={filter.end}
                onChange={(d: Dayjs | null) => filter.setEnd(d)}
                shouldDisableDate={(d: Dayjs) =>
                  filter.start ? d.isBefore(filter.start, "day") : false
                }
                slotProps={{ textField: { size: "small" } }}
              />
            </LocalizationProvider>
          )}

          {isAdmin && (
            <Select
              size="small"
              displayEmpty
              value={filter.consultant}
              onChange={(e) => filter.setConsultant(String(e.target.value))}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All consultants</MenuItem>
              {consultants.map((option) => (
                <MenuItem key={option._id} value={option._id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          )}

          {/* Compare toggle hidden 2026-08-31; ?compare=1 still works via URL */}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" sx={{ color: "#8a8a93" }}>
            {filter.rangeLabel}
            {filter.compare ? ` · vs ${filter.previousLabel}` : ""}
            {showsOwnFilters ? " · this tab isn't date-filtered" : ""}
          </Typography>
        </Stack>

        <Tabs
          value={activeTab}
          onChange={(_, value) => filter.setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mt: 1,
            "& .MuiTab-root": { fontWeight: 600, textTransform: "none" },
            "& .Mui-selected": { color: `${PRIMARY} !important` },
            "& .MuiTabs-indicator": { backgroundColor: PRIMARY },
          }}
        >
          {TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      <Box>
        {activeTab === "overview" && (
          <OverviewTab
            {...tabProps}
            isAdmin={Boolean(isAdmin)}
            compare={filter.compare}
            previousRange={filter.previous}
            previousLabel={filter.previousLabel}
          />
        )}
        {activeTab === "consultants" && (
          <ConsultantsTab
            {...tabProps}
            consultants={consultants}
            setConsultant={filter.setConsultant}
            currentUser={user}
            compare={filter.compare}
            previousRange={filter.previous}
            previousLabel={filter.previousLabel}
          />
        )}
        {activeTab === "clients" && <ClientsTab />}
        {activeTab === "resource-referrals" && (
          <ReferralsTab
            kind="resource"
            sub={filter.sub}
            setSub={filter.setSub}
          />
        )}
        {activeTab === "b2b-referrals" && (
          <ReferralsTab
            kind="provider"
            sub={filter.sub}
            setSub={filter.setSub}
          />
        )}
        {activeTab === "intake" && (
          <IntakeTab sub={filter.sub} setSub={filter.setSub} />
        )}
        {activeTab === "data" && <DataTab />}
      </Box>

      <DrilldownDrawer
        target={drilldown}
        start={filter.start}
        end={filter.end}
        consultant={filter.consultant}
        onClose={() => setDrilldown(null)}
      />
    </Box>
  );
};

export default ReportingLayout;
